'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import {
  ProductKindBadge,
  VerticalProductStatsPanel,
  isVerticalProductKind,
} from '@/config/marketplaceExtensions';
import { cartItemCount } from '@/platform/commerce/api';
import { ProductPurchaseControls } from '@/platform/marketplace/ProductPurchaseControls';
import type { SellerCart } from '@/platform/commerce/api';
import './vendor.css';
import {
  formatPrice,
  getVendorSettings,
  getVendorProduct,
  listLinkedClaims,
  listStorefrontPages,
  listVendorProducts,
  type ClaimPurchaseFields,
  type StorefrontPage,
  type VendorProduct,
  type VendorSettings,
} from './api';
import { ReportButton } from '@/platform/moderation/ReportButton';

export interface VendorProfileSectionProps {
  vendorUserId: string;
  isVerifiedSeller?: boolean;
  focusProductId?: string;
  openCart?: SellerCart | null;
  cartLoading?: boolean;
  onCartChange?: (cart: SellerCart | null) => void;
  onOpenCart?: () => void;
}

export function VendorStoreHeader({ vendorUserId }: VendorProfileSectionProps) {
  const [settings, setSettings] = useState<VendorSettings | null>(null);

  useEffect(() => {
    getVendorSettings(vendorUserId)
      .then(setSettings)
      .catch(() => setSettings(null));
  }, [vendorUserId]);

  if (!settings?.storeName || !settings.storePublic) return null;

  return (
    <section className="vendor-store-header">
      <h3>{settings.storeName}</h3>
      {settings.tagline && <p className="text-muted">{settings.tagline}</p>}
      {settings.shippingPolicy && (
        <details>
          <summary>Shipping</summary>
          <p>{settings.shippingPolicy}</p>
        </details>
      )}
      {settings.returnPolicy && (
        <details>
          <summary>Returns</summary>
          <p>{settings.returnPolicy}</p>
        </details>
      )}
    </section>
  );
}

export function VendorProductsList({
  vendorUserId,
  isVerifiedSeller = true,
  focusProductId,
  openCart = null,
  cartLoading = false,
  onCartChange,
  onOpenCart,
}: VendorProfileSectionProps) {
  const [products, setProducts] = useState<VendorProduct[]>([]);
  const [settings, setSettings] = useState<VendorSettings | null>(null);

  useEffect(() => {
    listVendorProducts(vendorUserId)
      .then((r) => setProducts(r.products.filter((p) => p.status === 'active')))
      .catch(() => setProducts([]));
    if (focusProductId) {
      getVendorProduct(vendorUserId, focusProductId)
        .then((product) => {
          if (product.status !== 'active') return;
          setProducts((prev) => {
            if (prev.some((p) => p.id === product.id)) return prev;
            return [product, ...prev];
          });
        })
        .catch(() => undefined);
    }
    getVendorSettings(vendorUserId)
      .then(setSettings)
      .catch(() => setSettings(null));
  }, [vendorUserId, focusProductId]);

  if (products.length === 0) return null;

  const cartCount = cartItemCount(openCart);

  return (
    <section className="vendor-public-products">
      <div className="vendor-public-products__header">
        <h3>
          Products{' '}
          {isVerifiedSeller && (
            <span className="vendor-console__badge vendor-console__badge--active">Verified seller</span>
          )}
        </h3>
        {isVerifiedSeller && cartCount > 0 && onOpenCart && (
          <button type="button" className="btn btn-secondary" onClick={onOpenCart}>
            View cart ({cartCount})
          </button>
        )}
      </div>
      {products.map((product) => (
        <article key={product.id} className="vendor-public-product">
          {product.imageUrls?.[0] && (
            <div className="vendor-public-product__media">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={product.imageUrls[0]} alt={contentImageAlt(product.name, 'product photo')} className="vendor-public-product__thumb" loading="lazy" />
              {(product.imageUrls?.length ?? 0) > 1 && (
                <span className="vendor-public-product__gallery-count">+{product.imageUrls!.length - 1}</span>
              )}
            </div>
          )}
          <strong>{product.name}</strong>
          {ProductKindBadge ? <ProductKindBadge kind={product.productKind} /> : null}
          <p className="vendor-public-product__price">{formatPrice(product.priceCents, product.currency)}</p>
          {product.description ? (
            <p className="vendor-public-product__description">{product.description}</p>
          ) : (
            <p className="text-muted">No description provided.</p>
          )}
          {isVerticalProductKind(product.productKind) && VerticalProductStatsPanel ? (
            <VerticalProductStatsPanel vendorUserId={vendorUserId} productId={product.id} />
          ) : null}
          {isVerifiedSeller && onCartChange && onOpenCart ? (
            <ProductPurchaseControls
              vendorUserId={vendorUserId}
              product={product}
              openCart={openCart}
              cartLoading={cartLoading}
              onCartChange={onCartChange}
              onOpenCart={onOpenCart}
            />
          ) : (
            <p className="text-muted">Seller verification pending.</p>
          )}
          <p>
            <ReportButton
              targetType="vendor_product"
              targetId={product.id}
              sellerUserId={vendorUserId}
              reportedUserId={vendorUserId}
              label="Report listing"
            />
          </p>
        </article>
      ))}
    </section>
  );
}

export function StorefrontPagesList({ vendorUserId }: VendorProfileSectionProps) {
  const [pages, setPages] = useState<StorefrontPage[]>([]);

  useEffect(() => {
    listStorefrontPages(vendorUserId)
      .then((r) => setPages(r.pages))
      .catch(() => setPages([]));
  }, [vendorUserId]);

  if (pages.length === 0) return null;

  return (
    <section className="vendor-storefronts">
      <h3>Our storefronts</h3>
      <ul>
        {pages.map((p) => (
          <li key={p.id}>
            <a href={p.url} target="_blank" rel="noopener noreferrer">
              {p.label || p.url}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function VendorLinkedClaimsFeed({ vendorUserId }: VendorProfileSectionProps) {
  const [claims, setClaims] = useState<ClaimPurchaseFields[]>([]);

  useEffect(() => {
    listLinkedClaims(vendorUserId)
      .then((r) => setClaims(r.claims))
      .catch(() => setClaims([]));
  }, [vendorUserId]);

  if (claims.length === 0) return null;

  return (
    <section className="vendor-linked-claims">
      <h3>Owner-approved claims</h3>
      <ul>
        {claims.map((c) => (
          <li key={c.id}>
            <Link href={`/claims/${c.id}`}>Claim {c.id}</Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
